import { decodeInputs, encodeInputs } from "../net/input-codec";
import { cleanPilotName } from "../net/protocol";
import { type GameMode, type MatchRules, normalizeRules, SIM_VERSION } from "../sim";
import { type MapDef, parseCustomMap } from "../sim/maps";
import { MAPS, type MapKey } from "./assets";

export { decodeInputs, encodeInputs };

/**
 * Replays (P10.6). A match is fully defined by its map, rules, mode, seed and every player's
 * per-tick inputs, because the sim is deterministic on every engine (P9.2). Inputs are 5-bit
 * numbers (`packControls`), stored as text: one character per value, run-length encoded.
 */
export interface Replay {
	format: "gravity-replay";
	/** Sim behaviour version: replays from another one would play out differently. */
	sim: number;
	/** Game version that recorded it (shown when a replay can't be played). */
	app: string;
	date: string;
	map: MapKey;
	rules: MatchRules;
	mode: GameMode;
	seed: number;
	/** Display names per player ("Blue", "Red (AI)", …). */
	pilots: string[];
	/** "local", "ai", "online" or "survival": where it came from (for the list). */
	kind: ReplayKind;
	/** Ticks recorded (the match length). */
	ticks: number;
	/** Run-length encoded inputs per player. */
	inputs: string[];
	/** The player-made map it was played on (P11.1), instead of `map`. */
	customMap?: MapDef;
	/** Survival with the 2008 spec's scoring (P11.5). */
	survivalScoring?: "2008";
	/** Pilot names by player ("" = the ship colour, P11.8), for the HUD during playback. */
	names?: string[];
}

export type ReplayKind = "local" | "ai" | "online" | "survival";

/** Replays kept in browser storage (oldest dropped first). */
export const MAX_STORED_REPLAYS = 5;
const STORAGE_KEY = "gravity.replays.v1";

export type ParsedReplay = { replay: Replay; inputs: number[][] } | { error: string };

/** Validates an untrusted replay (file, storage) and decodes its inputs. */
export function parseReplay(raw: unknown): ParsedReplay {
	if (typeof raw !== "object" || raw === null) return { error: "This isn't a Gravity replay." };
	const r = raw as Record<string, unknown>;
	if (r.format !== "gravity-replay") return { error: "This isn't a Gravity replay." };
	if (r.sim !== SIM_VERSION) {
		return {
			error: `This replay was recorded with Gravity ${typeof r.app === "string" ? r.app : "(another version)"}, whose physics differ from this version, so it can't be played back.`,
		};
	}
	const mode =
		r.mode === "duel" || r.mode === "survival" || r.mode === "koth" || r.mode === "ctf"
			? r.mode
			: null;
	const kind =
		r.kind === "local" || r.kind === "ai" || r.kind === "online" || r.kind === "survival"
			? r.kind
			: null;
	const map = typeof r.map === "string" && Object.hasOwn(MAPS, r.map) ? (r.map as MapKey) : null;
	const seed = Number.isSafeInteger(r.seed) ? (r.seed as number) : null;
	const ticks =
		Number.isSafeInteger(r.ticks) && (r.ticks as number) >= 0 ? (r.ticks as number) : null;
	const pilots =
		Array.isArray(r.pilots) && r.pilots.every((p) => typeof p === "string" && p.length <= 40)
			? (r.pilots as string[])
			: null;
	const encoded =
		Array.isArray(r.inputs) && r.inputs.every((s) => typeof s === "string")
			? (r.inputs as string[])
			: null;
	if (!mode || !kind || !map || seed === null || ticks === null || !pilots || !encoded) {
		return { error: "The replay file is damaged." };
	}
	let customMap: MapDef | undefined;
	if (r.customMap !== undefined) {
		try {
			customMap = parseCustomMap(r.customMap);
		} catch {
			return { error: "The replay's map is damaged." };
		}
	}
	const inputs = encoded.map(decodeInputs);
	const players = pilots.length;
	const countOk = mode === "survival" ? players === 1 : players >= 2 && players <= 4;
	if (!countOk || inputs.length !== players || inputs.some((i) => i === null)) {
		return { error: "The replay file is damaged." };
	}
	const replay: Replay = {
		format: "gravity-replay",
		sim: SIM_VERSION,
		app: typeof r.app === "string" ? r.app : "?",
		date: typeof r.date === "string" ? r.date : "",
		map,
		rules: normalizeRules(r.rules),
		mode,
		seed,
		pilots,
		kind,
		ticks,
		inputs: encoded,
		...(customMap ? { customMap } : {}),
		...(r.survivalScoring === "2008" && mode === "survival"
			? { survivalScoring: "2008" as const }
			: {}),
		...(Array.isArray(r.names) && r.names.length === players
			? { names: r.names.map(cleanPilotName) }
			: {}),
	};
	return { replay, inputs: inputs as number[][] };
}

/** Replays stored in this browser, newest first. Storage may be unavailable: then none. */
export function loadReplays(): Replay[] {
	try {
		const raw: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]");
		if (!Array.isArray(raw)) return [];
		return raw.flatMap((r) => {
			const parsed = parseReplay(r);
			return "replay" in parsed ? [parsed.replay] : [];
		});
	} catch {
		return [];
	}
}

/** Keeps a replay (newest first, at most MAX_STORED_REPLAYS); quietly drops it if storage is full. */
export function storeReplay(replay: Replay): void {
	saveReplays([replay, ...loadReplays()].slice(0, MAX_STORED_REPLAYS));
}

export function deleteReplay(index: number): void {
	const list = loadReplays();
	list.splice(index, 1);
	saveReplays(list);
}

function saveReplays(list: Replay[]): void {
	for (let n = list.length; n >= 0; n--) {
		try {
			localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, n)));
			return;
		} catch {
			// Quota: try again with fewer (older ones go first).
		}
	}
}

/** File name for a download, e.g. `gravity-2026-10-03-1542-classic.json`. */
export function replayFileName(replay: Replay): string {
	const d = replay.date.replace(/[-:T]/g, "").slice(0, 12);
	const stamp =
		d.length === 12
			? `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}-${d.slice(8, 12)}`
			: "replay";
	const map = replay.customMap ? "custom" : replay.map;
	return `gravity-${stamp}-${map}.json`;
}
